import { SceneSchema, pageSizeFor, spreadSizeFor } from '@kitup/book-schema';
import {
  BackCoverTemplate,
  BookCoverTemplate,
  renderSpread,
  type RenderableScene,
} from '@kitup/book-render';
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type JSX,
  type PointerEvent as ReactPointerEvent,
  type ReactNode,
  type SyntheticEvent,
} from 'react';
import type { BookPreviewDto, BookPreviewSceneDto } from '../api/book-preview.js';
import { useI18n } from '../i18n/context.js';
import { SpreadFlipViewer, type FlipSlot } from './SpreadFlipViewer.js';

const CSS_PX_PER_MM = 96 / 25.4;
const SWIPE_THRESHOLD_PX = 48;
const NARRATION_START_DELAY_MS = 2_000;

type ReaderPage =
  | { readonly kind: 'cover' }
  | { readonly kind: 'scene'; readonly scene: BookPreviewSceneDto }
  | { readonly kind: 'back' };

export function BookReader({ book }: { readonly book: BookPreviewDto }): JSX.Element {
  const { t } = useI18n();
  const rootRef = useRef<HTMLElement>(null);
  const swipeStart = useRef<number | null>(null);
  const reducedMotion = useReducedMotion();
  const [pageIndex, setPageIndex] = useState(0);
  const [mediaVisit, setMediaVisit] = useState(0);
  const [direction, setDirection] = useState<'forward' | 'backward'>('forward');
  const [fullscreen, setFullscreen] = useState(false);
  const audioRef = useRef<HTMLAudioElement>(null);
  const narration = book.attributes.reading.narration;
  /** Фрагмент текущего разворота уже играл: тогда кнопка продолжает, а не начинает. */
  const [narrationStarted, setNarrationStarted] = useState(false);
  const [narrationPlaying, setNarrationPlaying] = useState(false);
  const [narrationBlocked, setNarrationBlocked] = useState(false);
  const [imageReadyKey, setImageReadyKey] = useState<string | null>(null);
  const [videoReadyKey, setVideoReadyKey] = useState<string | null>(null);
  const [narrationReadyKey, setNarrationReadyKey] = useState<string | null>(null);
  const [narrationMuted, setNarrationMuted] = useState(
    () => readStorage(`kitup:narration-muted:${book.bookId}`) === 'true',
  );
  const pages = useMemo<readonly ReaderPage[]>(
    () => [
      { kind: 'cover' },
      ...book.scenes.map((scene) => ({ kind: 'scene' as const, scene })),
      { kind: 'back' },
    ],
    [book.scenes],
  );
  const lastIndex = pages.length - 1;
  const page = pages[pageIndex]!;
  /**
   * Разворот в css-пикселях: половина его ширины — это одна страница, вокруг
   * границы между ними и поворачивается лист.
   */
  const spreadPx = useMemo(() => {
    const size = spreadSizeFor(book.attributes);
    return { width: size.widthMm * CSS_PX_PER_MM, height: size.heightMm * CSS_PX_PER_MM };
  }, [book.attributes]);
  const leafWidthPx = spreadPx.width / 2;
  /**
   * `slide` и `fade` остаются простой сменой разворота целиком; настоящий
   * поворот листа — это `flip`, значение по умолчанию (01-SPEC.md §4).
   */
  const foldPages = book.attributes.reading.pageTurn === 'flip';
  /**
   * Тянуть лист имеет смысл, только когда он поворачивается. При выключенной
   * анимации жест остаётся простым свайпом по сцене — иначе на телефоне
   * пролистать книгу пальцем было бы нечем.
   */
  const dragPages = foldPages && !reducedMotion;
  const flipSlots = useMemo<readonly FlipSlot[]>(() => {
    if (!foldPages) return [];
    const leaf = (content: ReactNode): FlipSlot => ({ kind: 'leaf', content });
    const empty: FlipSlot = { kind: 'empty' };
    return [
      // За обложкой листа нет — слева от неё виден только фон сцены.
      empty,
      leaf(
        <SinglePageLeaf leafWidthPx={leafWidthPx} leafHeightPx={spreadPx.height}>
          <BookCoverTemplate
            attributes={book.attributes}
            imageUrl={book.cover?.url}
            imageAlt={t.preview.coverAlt(book.title)}
          />
        </SinglePageLeaf>,
      ),
      ...book.scenes.flatMap((scene) =>
        (['left', 'right'] as const).map((side) =>
          leaf(
            <SpreadHalfLeaf
              scene={scene}
              book={book}
              side={side}
              reducedMotion={reducedMotion}
              sceneAlt={t.preview.sceneAlt}
              leafWidthPx={leafWidthPx}
              leafHeightPx={spreadPx.height}
            />,
          ),
        ),
      ),
      leaf(
        <SinglePageLeaf leafWidthPx={leafWidthPx} leafHeightPx={spreadPx.height}>
          <BackCoverTemplate
            attributes={book.attributes}
            imageUrl={book.backCover?.url}
            imageAlt={t.preview.backCoverAlt(book.title)}
          />
        </SinglePageLeaf>,
      ),
      empty,
    ];
  }, [book, foldPages, leafWidthPx, reducedMotion, spreadPx.height, t.preview]);
  const currentScene = page.kind === 'scene' ? page.scene : null;
  const currentNarrationUrl = currentScene?.narration?.url;
  const currentImageUrl = currentScene?.image?.url;
  const currentVideoUrl =
    currentScene &&
    book.attributes.video.enabled &&
    book.attributes.reading.autoplayVideo &&
    !reducedMotion
      ? currentScene.video?.url
      : undefined;
  const currentMediaKey = currentScene
    ? [
        mediaVisit,
        currentScene.id,
        currentImageUrl ?? '',
        currentVideoUrl ?? '',
        currentNarrationUrl ?? '',
      ].join(':')
    : null;
  const visualMediaReady = Boolean(
    currentMediaKey &&
    (!currentImageUrl || imageReadyKey === currentMediaKey) &&
    (!currentVideoUrl || videoReadyKey === currentMediaKey),
  );
  const narrationMediaReady = Boolean(currentMediaKey && narrationReadyKey === currentMediaKey);

  const goTo = useCallback(
    (next: number) => {
      const clamped = Math.min(lastIndex, Math.max(0, next));
      if (pageIndex === clamped) return;
      setDirection(clamped > pageIndex ? 'forward' : 'backward');
      setPageIndex(clamped);
      setMediaVisit((current) => current + 1);
    },
    [lastIndex, pageIndex],
  );
  const previous = useCallback(() => goTo(pageIndex - 1), [goTo, pageIndex]);
  const next = useCallback(() => goTo(pageIndex + 1), [goTo, pageIndex]);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent): void => {
      if (isEditableTarget(event.target)) return;
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        previous();
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        next();
      } else if (event.key === 'Home') {
        event.preventDefault();
        goTo(0);
      } else if (event.key === 'End') {
        event.preventDefault();
        goTo(lastIndex);
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [goTo, lastIndex, next, previous]);

  useEffect(() => {
    const onFullscreen = (): void => setFullscreen(document.fullscreenElement === rootRef.current);
    document.addEventListener('fullscreenchange', onFullscreen);
    return () => document.removeEventListener('fullscreenchange', onFullscreen);
  }, []);

  useEffect(() => {
    for (const neighbor of [pages[pageIndex - 1], pages[pageIndex + 1]]) {
      if (neighbor?.kind !== 'scene') continue;
      if (neighbor.scene.image?.url) {
        const image = new Image();
        image.src = neighbor.scene.image.url;
      }
      if (neighbor.scene.video?.url && !reducedMotion) {
        const video = document.createElement('video');
        video.preload = 'metadata';
        video.src = neighbor.scene.video.url;
      }
      if (neighbor.scene.narration?.url && narration.enabled) {
        const audio = document.createElement('audio');
        audio.preload = 'metadata';
        audio.src = neighbor.scene.narration.url;
      }
    }
  }, [narration.enabled, pageIndex, pages, reducedMotion]);

  /**
   * Автозапуск со звуком браузер разрешает только после жеста на странице.
   * Отказ `play()` — не ошибка, а эта политика: фрагмент ждёт следующего жеста
   * и кнопки «Читать голосом», а не считается сломанным.
   */
  const playNarration = useCallback((fromStart: boolean): void => {
    const audio = audioRef.current;
    if (!audio?.getAttribute('src')) return;
    if (fromStart) audio.currentTime = 0;
    // Старые браузеры возвращают из `play()` не промис, а ничего.
    void Promise.resolve(audio.play()).then(
      () => {
        setNarrationStarted(true);
        setNarrationPlaying(true);
        setNarrationBlocked(false);
      },
      () => {
        setNarrationPlaying(false);
        setNarrationBlocked(true);
      },
    );
  }, []);

  useEffect(() => {
    if (!currentMediaKey || !currentNarrationUrl || !narration.enabled || !visualMediaReady) {
      return;
    }
    const timer = window.setTimeout(
      () => setNarrationReadyKey(currentMediaKey),
      NARRATION_START_DELAY_MS,
    );
    return () => window.clearTimeout(timer);
  }, [currentMediaKey, currentNarrationUrl, narration.enabled, visualMediaReady]);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    if (!audio.paused) audio.pause();
    audio.currentTime = 0;
    setNarrationStarted(false);
    setNarrationPlaying(false);
    if (!narration.enabled || !currentNarrationUrl) {
      audio.removeAttribute('src');
      setNarrationBlocked(false);
      return;
    }
    audio.src = currentNarrationUrl;
    audio.volume = narration.volume;
  }, [currentNarrationUrl, narration.enabled, narration.volume, pageIndex]);

  useEffect(() => {
    if (!narration.autoplay || !narrationMediaReady) return;
    playNarration(true);
  }, [narration.autoplay, narrationMediaReady, playNarration]);

  /**
   * Первый фрагмент запускаем из самого обработчика жеста: только так политика
   * автозапуска «разблокирует» элемент, и дальше развороты озвучиваются сами.
   */
  useEffect(() => {
    if (!narrationBlocked) return;
    const retry = (): void => playNarration(true);
    document.addEventListener('pointerdown', retry);
    document.addEventListener('keydown', retry);
    return () => {
      document.removeEventListener('pointerdown', retry);
      document.removeEventListener('keydown', retry);
    };
  }, [narrationBlocked, playNarration]);

  const toggleNarration = (): void => {
    if (!narrationPlaying) {
      playNarration(!narrationStarted);
      return;
    }
    audioRef.current?.pause();
    setNarrationPlaying(false);
  };
  const toggleNarrationMute = (): void => {
    const next = !narrationMuted;
    setNarrationMuted(next);
    writeStorage(`kitup:narration-muted:${book.bookId}`, String(next));
    if (audioRef.current) audioRef.current.muted = next;
  };

  const toggleFullscreen = (): void => {
    if (!document.fullscreenElement) {
      void rootRef.current?.requestFullscreen?.();
    } else {
      void document.exitFullscreen?.();
    }
  };
  const onPointerDown = (event: ReactPointerEvent): void => {
    swipeStart.current = event.clientX;
  };
  const onPointerUp = (event: ReactPointerEvent): void => {
    if (swipeStart.current === null) return;
    const distance = event.clientX - swipeStart.current;
    swipeStart.current = null;
    if (Math.abs(distance) < SWIPE_THRESHOLD_PX) return;
    if (distance < 0) next();
    else previous();
  };

  const pageLabel =
    page.kind === 'cover'
      ? t.preview.cover
      : page.kind === 'back'
        ? t.preview.backCover
        : t.preview.spread(page.scene.index + 1);

  const markCurrentVideoReady = (event: SyntheticEvent<HTMLElement>): void => {
    const target = event.target;
    if (
      !currentMediaKey ||
      !currentVideoUrl ||
      !(target instanceof HTMLVideoElement) ||
      !target.hasAttribute('data-illustration-video') ||
      target.getAttribute('src') !== currentVideoUrl
    ) {
      return;
    }
    setVideoReadyKey(currentMediaKey);
  };

  return (
    <section
      ref={rootRef}
      className="kitup-reader"
      data-reduced-motion={reducedMotion ? 'true' : 'false'}
      aria-label={t.preview.readerLabel(book.title)}
      onCanPlayCapture={markCurrentVideoReady}
      onErrorCapture={markCurrentVideoReady}
    >
      {currentMediaKey && currentImageUrl ? (
        <img
          key={currentMediaKey}
          src={currentImageUrl}
          alt=""
          aria-hidden="true"
          data-narration-image-preload=""
          onLoad={() => setImageReadyKey(currentMediaKey)}
          onError={() => setImageReadyKey(currentMediaKey)}
          className="pointer-events-none absolute h-px w-px opacity-0"
        />
      ) : null}
      <header className="kitup-reader-header">
        <div className="min-w-0">
          <h1 className="truncate text-lg font-semibold" lang={book.language}>
            {book.title}
          </h1>
          <p className="text-sm text-[var(--kitup-color-muted)]" aria-live="polite">
            {pageLabel} · {t.preview.counter(pageIndex + 1, pages.length)}
          </p>
        </div>
        {book.attributes.reading.fullscreen ? (
          <button
            type="button"
            className="kitup-secondary-button shrink-0"
            onClick={toggleFullscreen}
            aria-pressed={fullscreen}
          >
            {fullscreen ? t.preview.exitFullscreen : t.preview.fullscreen}
          </button>
        ) : null}
      </header>

      <div
        className="kitup-reader-stage"
        {...(dragPages
          ? // Страницу тянут за край: обработчик живёт на самом листе,
            // а второй, на сцене, листал бы разворот дважды за один жест.
            {}
          : {
              onPointerDown,
              onPointerUp,
              onPointerCancel: () => {
                swipeStart.current = null;
              },
            })}
      >
        <button
          type="button"
          className="kitup-reader-arrow kitup-reader-arrow-left"
          onClick={previous}
          disabled={pageIndex === 0}
          aria-label={t.preview.previous}
        >
          ‹
        </button>
        {foldPages ? (
          <SpreadFlipViewer
            slots={flipSlots}
            leafWidthPx={leafWidthPx}
            leafHeightPx={spreadPx.height}
            viewIndex={pageIndex}
            onViewChange={goTo}
            reducedMotion={reducedMotion}
          />
        ) : (
          <div
            key={`${page.kind}:${page.kind === 'scene' ? page.scene.id : page.kind}`}
            className={`kitup-reader-turn kitup-reader-turn-${book.attributes.reading.pageTurn}`}
            data-direction={direction}
            data-reader-page={page.kind}
          >
            <ReaderPageView page={page} book={book} reducedMotion={reducedMotion} />
          </div>
        )}
        <button
          type="button"
          className="kitup-reader-arrow kitup-reader-arrow-right"
          onClick={next}
          disabled={pageIndex === lastIndex}
          aria-label={t.preview.next}
        >
          ›
        </button>
      </div>

      <nav className="kitup-reader-controls" aria-label={t.preview.navigationLabel}>
        <input
          type="range"
          min={0}
          max={lastIndex}
          value={pageIndex}
          onChange={(event) => goTo(Number(event.target.value))}
          aria-label={t.preview.pagePicker}
        />
        {narration.enabled && currentNarrationUrl ? (
          <div className="kitup-reader-narration-controls">
            <button
              type="button"
              className="kitup-reader-icon-button kitup-secondary-button"
              disabled={!narrationMediaReady}
              aria-label={
                narrationPlaying
                  ? t.preview.pauseNarration
                  : narrationStarted
                    ? t.preview.resumeNarration
                    : t.preview.playNarration
              }
              title={
                narrationPlaying
                  ? t.preview.pauseNarration
                  : narrationStarted
                    ? t.preview.resumeNarration
                    : t.preview.playNarration
              }
              onClick={toggleNarration}
            >
              <NarrationToggleIcon playing={narrationPlaying} />
            </button>
            <button
              type="button"
              className="kitup-reader-icon-button kitup-quiet-button"
              disabled={!narrationMediaReady}
              onClick={() => playNarration(true)}
              aria-label={t.preview.repeatNarration}
              title={t.preview.repeatNarration}
            >
              <RepeatIcon />
            </button>
            <button
              type="button"
              className="kitup-reader-icon-button kitup-quiet-button"
              onClick={toggleNarrationMute}
              aria-pressed={narrationMuted}
              aria-label={narrationMuted ? t.preview.unmuteNarration : t.preview.muteNarration}
              title={narrationMuted ? t.preview.unmuteNarration : t.preview.muteNarration}
            >
              <VolumeIcon muted={narrationMuted} />
            </button>
          </div>
        ) : null}
      </nav>
      <audio
        ref={audioRef}
        muted={narrationMuted}
        preload="metadata"
        onPlay={() => setNarrationPlaying(true)}
        onPause={() => setNarrationPlaying(false)}
        onEnded={() => {
          setNarrationPlaying(false);
          // Дочитанный фрагмент продолжать нечем — кнопка снова читает с начала.
          setNarrationStarted(false);
        }}
      />
      <p className="kitup-reader-help">{t.preview.help}</p>
    </section>
  );
}

function RepeatIcon(): JSX.Element {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 24 24"
      width="20"
      height="20"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.9"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M3 12a9 9 0 1 0 3-7.7L3 7" />
      <path d="M3 3v4h4" />
    </svg>
  );
}

function NarrationToggleIcon({ playing }: { readonly playing: boolean }): JSX.Element {
  return (
    <svg aria-hidden="true" viewBox="0 0 24 24" width="20" height="20" fill="currentColor">
      {playing ? (
        <>
          <rect x="6.5" y="5" width="4" height="14" rx="1" />
          <rect x="13.5" y="5" width="4" height="14" rx="1" />
        </>
      ) : (
        <path d="M8 5.6v12.8a1 1 0 0 0 1.55.83l9-6.4a1 1 0 0 0 0-1.66l-9-6.4A1 1 0 0 0 8 5.6Z" />
      )}
    </svg>
  );
}

function VolumeIcon({ muted }: { readonly muted: boolean }): JSX.Element {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 24 24"
      width="20"
      height="20"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.9"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M11 5 6.8 8.5H3.5v7h3.3L11 19z" />
      {muted ? (
        <>
          <path d="m16 9 5 6" />
          <path d="m21 9-5 6" />
        </>
      ) : (
        <>
          <path d="M15 9.5a4 4 0 0 1 0 5" />
          <path d="M18 6.8a8 8 0 0 1 0 10.4" />
        </>
      )}
    </svg>
  );
}

function ReaderPageView({
  page,
  book,
  reducedMotion,
}: {
  readonly page: ReaderPage;
  readonly book: BookPreviewDto;
  readonly reducedMotion: boolean;
}): JSX.Element {
  const { t } = useI18n();
  const attributes = book.attributes;
  if (page.kind === 'cover') {
    const size = pageSizeFor(attributes);
    return (
      <ScaledBookPage widthMm={size.widthMm} heightMm={size.heightMm}>
        <BookCoverTemplate
          attributes={attributes}
          imageUrl={book.cover?.url}
          imageAlt={t.preview.coverAlt(book.title)}
        />
      </ScaledBookPage>
    );
  }
  if (page.kind === 'back') {
    const size = pageSizeFor(attributes);
    return (
      <ScaledBookPage widthMm={size.widthMm} heightMm={size.heightMm}>
        <BackCoverTemplate
          attributes={attributes}
          imageUrl={book.backCover?.url}
          imageAlt={t.preview.backCoverAlt(book.title)}
        />
      </ScaledBookPage>
    );
  }

  const size = spreadSizeFor(attributes);
  const scene = toRenderableScene(page.scene, book, reducedMotion, t.preview.sceneAlt);
  return (
    <ScaledBookPage widthMm={size.widthMm} heightMm={size.heightMm}>
      {/*
        The spread's text is in the book's language, which need not be the interface's: a
        screen reader reads a French book in French. Only the spread: the reader's controls
        around it stay in the interface's language.
      */}
      <div lang={book.language}>{renderSpread(scene, attributes)}</div>
    </ScaledBookPage>
  );
}

/** Обложка занимает одну страницу — в развороте она стоит справа, по центру листа. */
function SinglePageLeaf({
  leafWidthPx,
  leafHeightPx,
  children,
}: {
  readonly leafWidthPx: number;
  readonly leafHeightPx: number;
  readonly children: ReactNode;
}): JSX.Element {
  return (
    <div
      style={{
        display: 'grid',
        placeItems: 'center',
        width: leafWidthPx,
        height: leafHeightPx,
        overflow: 'hidden',
      }}
    >
      {children}
    </div>
  );
}

/**
 * Половина разворота: тот же разворот целиком, обрезанный окном в страницу
 * шириной и сдвинутый влево для правой половины. Иначе текст и иллюстрация,
 * пересекающие корешок, разъехались бы на стыке.
 */
function SpreadHalfLeaf({
  scene,
  book,
  side,
  reducedMotion,
  sceneAlt,
  leafWidthPx,
  leafHeightPx,
}: {
  readonly scene: BookPreviewSceneDto;
  readonly book: BookPreviewDto;
  readonly side: 'left' | 'right';
  readonly reducedMotion: boolean;
  readonly sceneAlt: (index: number, title: string) => string;
  readonly leafWidthPx: number;
  readonly leafHeightPx: number;
}): JSX.Element {
  const renderable = toRenderableScene(scene, book, reducedMotion, sceneAlt);
  /**
   * Иллюстрация разворота одна, а половины две. Правую убираем из дерева
   * доступности и оставляем без alt: озвучивать одну картинку дважды незачем.
   */
  const half = side === 'right' ? { ...renderable, imageAlt: '' } : renderable;

  return (
    <div
      data-reader-half={side}
      {...(side === 'right' ? { 'aria-hidden': 'true' as const } : {})}
      style={{
        position: 'relative',
        width: leafWidthPx,
        height: leafHeightPx,
        overflow: 'hidden',
      }}
    >
      <div
        lang={book.language}
        style={{ position: 'absolute', top: 0, left: side === 'left' ? 0 : -leafWidthPx }}
      >
        {renderSpread(half, book.attributes)}
      </div>
    </div>
  );
}

function ScaledBookPage({
  widthMm,
  heightMm,
  children,
}: {
  readonly widthMm: number;
  readonly heightMm: number;
  readonly children: ReactNode;
}): JSX.Element {
  const host = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(1);
  const widthPx = widthMm * CSS_PX_PER_MM;
  const heightPx = heightMm * CSS_PX_PER_MM;

  useLayoutEffect(() => {
    const element = host.current;
    if (!element) return;
    const resize = (): void => {
      const bounds = element.getBoundingClientRect();
      if (!bounds.width || !bounds.height) return;
      setScale(Math.min(1, bounds.width / widthPx, bounds.height / heightPx));
    };
    resize();
    if (typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(resize);
    observer.observe(element);
    return () => observer.disconnect();
  }, [heightPx, widthPx]);

  return (
    <div ref={host} className="kitup-reader-fit-host">
      <div style={{ width: widthPx * scale, height: heightPx * scale }}>
        <div
          style={{
            width: widthPx,
            height: heightPx,
            transform: `scale(${scale})`,
            transformOrigin: 'top left',
          }}
        >
          {children}
        </div>
      </div>
    </div>
  );
}

function toRenderableScene(
  scene: BookPreviewSceneDto,
  book: BookPreviewDto,
  reducedMotion: boolean,
  alt: (index: number, title: string) => string,
): RenderableScene {
  const parsed = SceneSchema.parse({
    id: scene.id,
    bookId: book.bookId,
    index: scene.index,
    layoutId: scene.layoutId,
    textRu: scene.textRu,
    textBlocks: scene.textBlocks,
    bandImageRatio: scene.bandImageRatio,
  });
  return {
    ...parsed,
    imageUrl: scene.image?.url,
    imageAlt: alt(scene.index + 1, book.title),
    videoUrl:
      book.attributes.video.enabled && book.attributes.reading.autoplayVideo && !reducedMotion
        ? scene.video?.url
        : null,
  };
}

function useReducedMotion(): boolean {
  const [reduced, setReduced] = useState(false);
  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return;
    const media = window.matchMedia('(prefers-reduced-motion: reduce)');
    const update = (): void => setReduced(media.matches);
    update();
    media.addEventListener?.('change', update);
    return () => media.removeEventListener?.('change', update);
  }, []);
  return reduced;
}

function isEditableTarget(target: EventTarget | null): boolean {
  return (
    target instanceof HTMLInputElement ||
    target instanceof HTMLTextAreaElement ||
    target instanceof HTMLSelectElement
  );
}

function readStorage(key: string): string | null {
  try {
    return window.localStorage?.getItem(key) ?? null;
  } catch {
    return null;
  }
}

function writeStorage(key: string, value: string): void {
  try {
    window.localStorage?.setItem(key, value);
  } catch {
    // Приватный режим и встроенные браузеры могут запрещать storage.
  }
}
